import React from 'react';
import { THREE, AssetLibraryManager, MaterialConfig, TextureSlotConfig } from '@easytwin/runtime';
import { MaterialEditorConfig, TEXTURE_SLOT_NAME_MAP, TextureSlotSelect } from './type';

export interface TextureOption {
  key: string;
  label: React.ReactNode;
  type: 'original' | 'scene'; // glb内部纹理或场景纹理
  img?: string;
}

export interface TextureUVConfig {
  wrapS: number;
  wrapT: number;
  repeat: { x: number; y: number };
  offset: { x: number; y: number };
  rotation: number;
  offsetSpeed: { x: number; y: number };
}

/**
 * 材质配置构建器，用于材质配置项构建为UI层使用的配置
 */
export class MaterialConfigBuilder {
  private material: THREE.Material;
  private assetLibrary: AssetLibraryManager;

  constructor({
    material,
    assetLibrary,
  }: {
    material: THREE.Material;
    assetLibrary: AssetLibraryManager;
  }) {
    this.material = material;
    this.assetLibrary = assetLibrary;
  }

  /**
   * 构建材质编辑器的配置项
   * @param material
   * @returns
   */
  buildMaterialEditorConfig(
    material: THREE.Material,
    type: 'SceneMaterial' | 'ModelMaterial',
  ): MaterialEditorConfig {
    const editorConfig: MaterialEditorConfig = {
      textureSlots: {},
    } as MaterialEditorConfig;
    const materialConfig = this.assetLibrary.getMaterialConfig(material);

    for (const key in materialConfig) {
      // 处理纹理类型的属性
      if (key.toLowerCase().endsWith('map')) {
        const currentTexture = (material as any)[key];
        const originalTexture = (material as any)[`${key}_original`];

        // 获取包含原始纹理和场景纹理的选项列表
        const textureOptions = this.getTextureOptions(
          type === 'ModelMaterial' ? originalTexture : undefined,
        );

        // 构建纹理槽位配置
        const slotConfig: TextureSlotSelect = {
          label: TEXTURE_SLOT_NAME_MAP[key as keyof typeof TEXTURE_SLOT_NAME_MAP]?.label ?? key,
          options: textureOptions,
          value: currentTexture?.uuid ?? null,
          config: currentTexture
            ? this.getTextureUVConfig(currentTexture)
            : {
                enable: false,
                rotation: 0,
                repeat: { x: 1, y: 1 },
                offset: { x: 0, y: 0 },
                offsetSpeed: { x: 0, y: 0 },
                wrapS: THREE.RepeatWrapping,
                wrapT: THREE.RepeatWrapping,
                channel: 0,
                colorSpace: key === 'map' ? 'srgb' : '',
              },
        };

        editorConfig.textureSlots[key as keyof typeof editorConfig.textureSlots] = slotConfig;
      } else {
        // 处理非纹理类型的属性
        (editorConfig as any)[key] = (materialConfig as any)[key];
      }
    }

    return editorConfig;
  }

  /**
   * 构建材质配置，将编辑器配置转换为材质配置，用于后端存储
   * @param editorConfig
   * @returns
   */
  buildMaterialConfig(editorConfig: MaterialEditorConfig): MaterialConfig {
    const materialConfig: MaterialConfig = {} as MaterialConfig;

    for (const key in editorConfig.textureSlots) {
      const slot = (editorConfig.textureSlots as any)[key] as TextureSlotSelect;
      if (slot.value) {
        const texture = this.assetLibrary.getTexture(slot.value);
        if (texture) {
          // 纹理槽位开启且纹理槽位引用的是glb外部纹理
          const textureSlotConfig: TextureSlotConfig = {
            enable: true,
            $ref: texture.uuid,
            rotation: slot.config?.rotation ?? 0,
            repeat: { x: slot.config?.repeat?.x ?? 1, y: slot.config?.repeat?.y ?? 1 },
            offset: { x: slot.config?.offset?.x ?? 0, y: slot.config?.offset?.y ?? 0 },
            offsetSpeed: {
              x: slot.config?.offsetSpeed?.x ?? 0,
              y: slot.config?.offsetSpeed?.y ?? 0,
            },
            wrapS: slot.config?.wrapS ?? THREE.RepeatWrapping,
            wrapT: slot.config?.wrapT ?? THREE.RepeatWrapping,
            channel: slot.config?.channel ?? 0,
            colorSpace: slot.config?.colorSpace ?? '',
          };
          (materialConfig as any)[key] = textureSlotConfig;
        } else {
          // 使用glb内部纹理
          (materialConfig as any)[key] = {
            enable: true,
            rotation: slot.config?.rotation ?? 0,
            repeat: { x: slot.config?.repeat?.x ?? 1, y: slot.config?.repeat?.y ?? 1 },
            offset: { x: slot.config?.offset?.x ?? 0, y: slot.config?.offset?.y ?? 0 },
            offsetSpeed: {
              x: slot.config?.offsetSpeed?.x ?? 0,
              y: slot.config?.offsetSpeed?.y ?? 0,
            },
            wrapS: slot.config?.wrapS ?? THREE.RepeatWrapping,
            wrapT: slot.config?.wrapT ?? THREE.RepeatWrapping,
            channel: slot.config?.channel ?? 0,
            colorSpace: slot.config?.colorSpace ?? '',
          };
        }
      } else {
        // 纹理槽位关闭
        (materialConfig as any)[key] = {
          enable: false,
          rotation: slot.config?.rotation ?? 0,
          repeat: { x: slot.config?.repeat.x ?? 1, y: slot.config?.repeat.y ?? 1 },
          offset: { x: slot.config?.offset.x ?? 0, y: slot.config?.offset.y ?? 0 },
          offsetSpeed: { x: slot.config?.offsetSpeed.x ?? 0, y: slot.config?.offsetSpeed.y ?? 0 },
          wrapS: slot.config?.wrapS ?? THREE.RepeatWrapping,
          wrapT: slot.config?.wrapT ?? THREE.RepeatWrapping,
          channel: slot.config?.channel ?? 0,
          colorSpace: slot.config?.colorSpace ?? '',
        };
      }
    }

    // 处理非纹理类型的属性
    for (const key in editorConfig) {
      if (key !== 'textureSlots') {
        (materialConfig as any)[key] = (editorConfig as any)[key];
      }
    }

    return materialConfig;
  }

  private getTextureOptions(originalTexture?: THREE.Texture): TextureOption[] {
    const options: TextureOption[] = [];

    // 1. 添加原始纹理
    if (originalTexture) {
      options.push({
        key: originalTexture.uuid,
        label: originalTexture.name,
        type: 'original',
        img: this.assetLibrary.defaultTexturePreview,
      });
    }

    // 2. 添加场景纹理
    const sceneTextures = this.assetLibrary.getTextureList();
    sceneTextures.forEach((texture) => {
      options.push({
        key: texture.uuid,
        label: texture.name,
        type: 'scene',
        img: this.assetLibrary.defaultTexturePreview,
      });
    });

    return options;
  }

  private getTextureUVConfig(texture: THREE.Texture): TextureSlotConfig {
    return {
      enable: true,
      rotation: texture.rotation,
      repeat: {
        x: texture.repeat.x,
        y: texture.repeat.y,
      },
      offset: {
        x: texture.offset.x,
        y: texture.offset.y,
      },
      offsetSpeed: {
        x: texture.offsetSpeed.x,
        y: texture.offsetSpeed.y,
      },
      wrapS: texture.wrapS,
      wrapT: texture.wrapT,
      colorSpace: texture.colorSpace,
      channel: texture.channel,
    };
  }
}
